<template>
  <div class="w-full">
    <div v-if="!analysisFinished" class="text-12 text-red-500 my-8 bg-red-50 p-4 rounded-6 flex items-center">
      <img src="@/assets/img/red_exclamation_mark.svg" class="w-16 h-16 mr-8" />
      <span>Values are estimations. Please calculate for more accuracy.</span>
    </div>
    <div class="flex justify-start items-baseline pb-20">
      <Toggle :possible-classes="carbonData.levels" :selected-class="selectedLevel" @selected="selectLevel($event)" />
    </div>
    <hr class="border-gray-50 mb-16" />
    <div class="flex flex-col gap-20 pb-16">
      <div class="flex flex-col gap-8">
        <div class="text-14 text-gray-900">Material production</div>
        <div class="flex items-center justify-between">
          <div class="text-13 text-gray-600">Energy consumption</div>
          <div class="flex items-center gap-4">
            <div class="text-13 text-gray-1000 font-bold">
              {{ $formatTwoDecimalPlaces(carbonData.materialEnergyConsumption) }}
            </div>
            <div class="text-13 text-gray-400">kg CO<sub>2</sub></div>
          </div>
        </div>
        <div class="flex items-center justify-between">
          <div class="text-13 text-gray-600">Consumables</div>
          <div class="flex items-center gap-4">
            <div class="text-13 text-gray-1000 font-bold">-</div>
            <div class="text-13 text-gray-400">kg CO<sub>2</sub></div>
          </div>
        </div>
      </div>
      <hr class="border-gray-50 mb-8" />
      <div class="flex flex-col gap-8">
        <div class="text-14 text-gray-900">Part production</div>
        <div class="flex items-center justify-between">
          <div class="text-13 text-gray-600">Energy consumption</div>
          <div class="flex items-center gap-4">
            <div class="text-13 text-gray-1000 font-bold">
              {{ $formatTwoDecimalPlaces(carbonData.partEnergyConsumption) }}
            </div>
            <div class="text-13 text-gray-400">kg CO<sub>2</sub></div>
          </div>
        </div>
        <div class="flex items-center justify-between">
          <div class="text-13 text-gray-600">Consumables</div>
          <div class="flex items-center gap-4">
            <div class="text-13 text-gray-1000 font-bold">-</div>
            <div class="text-13 text-gray-400">kg CO<sub>2</sub></div>
          </div>
        </div>
      </div>
      <hr class="border-gray-50 mb-8" />
      <div class="flex items-center justify-end gap-4">
        <div class="text-14 text-gray-900 font-bold">Total</div>
        <div class="flex items-center gap-8">
          <div class="text-14 text-gray-1000 font-bold">{{ $formatTwoDecimalPlaces(carbonData.totalCo2) }}</div>
          <div class="text-14 text-gray-400">kg CO<sub>2</sub></div>
        </div>
      </div>
    </div>
    <CarbonChartOneChain
      v-if="
        carbonData.segments && carbonData.segments.length && $formatTwoDecimalPlaces(carbonData.totalCo2) !== '0,00'
      "
      :segments="carbonData.segments"
      :total-segment-index="totalSegmentIndex"
      :total-value="$formatTwoDecimalPlaces(carbonData.totalCo2)"
      :total-unit="'kg CO₂-eq'"
      :title="part.name + ' - ' + activeProcessChain.name"
    />
    <hr class="border-gray-50 mb-8" />
    <ProcessChain
      :chain="activeProcessChain"
      editable
      :selected-level="selectedLevel"
      property="co2"
      :materialEnergyConsumption="carbonData.materialEnergyConsumption"
    />
  </div>
</template>

<script>
import { mapState } from 'vuex';

import ProcessChain from '@/components/PartWorkflow/ProcessChain/ProcessChain.vue';
import CarbonChartOneChain from '@/components/PartWorkflow/Comparison/tablecells/carbon/components/CarbonChartOneChain.vue';
import Toggle from '@/components/Reusable/Toggle.vue';
import { analysisFinished } from '@/composables/processChainHandling.js';
import { processCarbonFootprintData } from '@/utils/carbonCalculation';

export default {
  name: 'CarbonFootprint',

  components: {
    ProcessChain,
    Toggle,
    CarbonChartOneChain,
  },

  data() {
    return {
      selectedLevel: {
        _pp: 'Part',
      },
      totalSegmentIndex: 0,
    };
  },

  computed: {
    ...mapState(['investigationDetails', 'part']),

    analysisFinished() {
      return analysisFinished(this.activeInvestigationDetails.uid);
    },

    activeInvestigationDetails() {
      return this.investigationDetails;
    },

    activeProcessChain() {
      let allChains = this.part.process_chains;
      return allChains[this.activeInvestigationDetails.uid];
    },

    carbonData() {
      return processCarbonFootprintData(this.activeProcessChain, this.selectedLevel);
    },

    materialEnergyConsumption() {
      return this.carbonData.materialEnergyConsumption;
    },

    partEnergyConsumption() {
      return this.carbonData.partEnergyConsumption;
    },

    totalCo2() {
      return this.carbonData.totalCo2;
    },

    segments() {
      return this.carbonData.segments;
    },

    levels() {
      return this.carbonData.levels;
    },
  },

  methods: {
    selectLevel(event) {
      this.selectedLevel = event;
    },
  },
};
</script>
